import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { authFetch } from "../../../services/authFetch";
import { socketService } from "../../../services/socket";
import { useAuth } from "../../auth/contexts/AuthContext";
import { useProjects } from "./ProjectContext";
import { Sentry } from "../../../sentry";
// Define the type for a selectable item
export interface SelectableItem {
  id: string;
  type: "story" | "log" | "transcript" | "otf";
  title: string;
  thumbnail?: string;
  duration?: string;
  sourceId?: string;
  // Enhanced metadata for detailed view
  tc?: string;
  author?: string;
  tags?: string[];
  rating?: number;
  participants?: string[];
  // Cutting Module: Timecode fields
  tcIn?: string; // ISO timestamp string (e.g. "2025-10-23T21:38:51.59")
  tcOut?: string; // ISO timestamp string
  tcInMs?: number; // Milliseconds offset within source audio
  tcOutMs?: number; // Milliseconds offset within source audio
  speakerName?: string; // Participant name (e.g. "MÍSTICO")
  speakerOrder?: number; // ISO number (e.g. 47)
  directorId?: string; // Director selection ID
  storyDirectorId?: string; // Story director selection ID
  selectedText?: string; // The selected text fragment
  words?: any[]; // The list of word objects for transcripts
  sourceSegmentId?: string; // ID of the source segment
  storyId?: string; // Story this item belongs to
  rundownId?: string; // Server-resolved at insert; used to group VTR exports
  rundownName?: string; // Snapshot of rundown name at insert time
  // Propagated metadata from source stories/events
  storyTags?: string[];
  storyParticipants?: string[];
  storyRating?: number;
  storyName?: string;
  eventName?: string;
  entryTags?: string[];
  streams?: string[];
  // Canal del relay asociado al asset. Se conserva junto con `streams` para
  // que un corte reproduzca la señal correcta, no sólo la cámara por defecto
  // de la historia.
  relayChannel?: string;
  cameras?: string[];
  originType?: string;
  startAt?: string | number;
  createdAt?: string | number;
  endAt?: string;
}

// Navigate-to-source target (from CUT to EXPLORE)
export interface NavigateTarget {
  storyId: string;
  itemId: string;
  itemType: "log" | "transcript";
}

// Assembly Project type
export interface AssemblyProject {
  id: string;
  name: string;
  items: SelectableItem[];
  createdAt: number;
  updatedAt: number;
  folderId?: string | null;
  projectId?: string | null;
  isApproved?: boolean;
  createdBy?: string | null;
  vtrTags?: string[];
  vtrStars?: number;
  vtrDescription?: string;
  vtrGrafismo?: string;
  vtrNotas?: string;
  vtrEditingStatus?: string;
  // Plate document for the Cortes editor. Untyped here to avoid coupling
  // the selection context to the editor's schema; the editor casts on read.
  cortesDoc?: unknown[] | null;
}

// Assembly Folder type
export interface AssemblyFolder {
  id: string;
  name: string;
  createdAt: number;
  isExpanded?: boolean;
  parentId?: string | null;
}

interface SelectionContextType {
  // Selection Mode
  isSelectionMode: boolean;
  toggleSelectionMode: () => void;

  // Current selection (staging area before adding to project)
  selectedItems: SelectableItem[];
  toggleItem: (item: SelectableItem) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
  moveItem: (fromIndex: number, toIndex: number) => void;

  // Assembly Modal (legacy, still useful for floating preview)
  isAssemblyModalOpen: boolean;
  toggleAssemblyModal: () => void;

  // Assembly Projects
  assemblyProjects: AssemblyProject[];
  activeProjectId: string | null;
  setActiveProject: (id: string | null) => void;
  createProject: (name: string, folderId?: string | null) => AssemblyProject;
  deleteProject: (id: string) => void;
  renameProject: (id: string, name: string) => void;
  updateProject: (id: string, updates: Partial<AssemblyProject>) => void;
  addItemsToProject: (
    projectId: string,
    items: SelectableItem[],
    insertAtIndex?: number,
  ) => { added: number; skipped: number };
  removeItemFromProject: (projectId: string, itemId: string) => void;
  updateItemInProject: (
    projectId: string,
    itemId: string,
    updates: Partial<SelectableItem>,
  ) => void;
  moveItemInProject: (projectId: string, fromIndex: number, toIndex: number) => void;
  getActiveProject: () => AssemblyProject | null;

  // Assembly Folders
  assemblyFolders: AssemblyFolder[];
  activeFolderId: string | null;
  setActiveFolder: (id: string | null) => void;
  createFolder: (name: string, parentId?: string | null) => AssemblyFolder;
  deleteFolder: (id: string) => void;
  renameFolder: (id: string, name: string) => void;
  toggleFolderExpanded: (id: string) => void;
  moveProjectToFolder: (projectId: string, folderId: string | null) => void;
  moveFolderToFolder: (folderId: string, parentId: string | null) => void;

  // Navigate to source
  navigateToSource: NavigateTarget | null;
  setNavigateToSource: (target: NavigateTarget) => void;
  clearNavigateToSource: () => void;

  // Loading state
  isLoaded: boolean;

  // Fetch from Backend
  fetchProjects: (approvedOnly?: boolean) => Promise<void>;
  // Day scope for the CUT list. The list only ever renders one day, so the
  // window is pushed to the backend instead of filtering in the browser.
  setCutDayWindow: (window: { from: number; to: number } | null) => void;
}

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

// Helper to generate unique IDs
const generateId = () => `proj_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;
const generateFolderId = () => `fold_${Date.now()}_${crypto.randomUUID().substring(0, 8)}`;

export const SelectionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectableItem[]>([]);
  const [isAssemblyModalOpen, setIsAssemblyModalOpen] = useState(false);
  const [navigateToSource, setNavigateToSourceState] = useState<NavigateTarget | null>(null);

  const [assemblyFolders, setAssemblyFolders] = useState<AssemblyFolder[]>([]);
  const [assemblyProjects, setAssemblyProjects] = useState<AssemblyProject[]>([]);
  const assemblyProjectsRef = useRef<AssemblyProject[]>(assemblyProjects);
  assemblyProjectsRef.current = assemblyProjects;
  const [activeProjectId, setActiveProjectId] = useState<string | null>(() => {
    if (typeof window !== "undefined") {
      try {
        // En lugar de depender únicamente de prodProjectId en el estado reactivo que puede tardar en resolverse
        // leemos directamente el localStorage actual para encontrar el ID del proyecto de producción activo en el cliente.
        const storedProd = localStorage.getItem("braindog_prodProjectId");
        let pId = null;
        if (storedProd) {
          try {
            const parsed = JSON.parse(storedProd);
            pId = typeof parsed === "object" ? parsed?.id : parsed;
          } catch (err) {
            pId = storedProd;
          }
        }
        const lastActive = localStorage.getItem(`braindog_last_cut_${pId || "global"}`);
        if (lastActive) {
          console.debug(`[Selection] Sync restore activeProjectId (initial): ${lastActive}`);
          return lastActive;
        }
      } catch (e) {}
    }
    return null;
  });
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentFilterApprovedOnly, setCurrentFilterApprovedOnly] = useState(false);

  const { activeProject: currentProdProject } = useProjects();
  const prodProjectId = currentProdProject?.id;

  // Sincronizar activeProjectId de forma reactiva y síncrona en el cliente tan pronto como prodProjectId cambie
  useEffect(() => {
    if (typeof window !== "undefined") {
      const lastActive = localStorage.getItem(`braindog_last_cut_${prodProjectId || "global"}`);
      if (lastActive) {
        console.debug(
          `[Selection] Reactively switching activeProjectId to: ${lastActive} for production ${prodProjectId}`,
        );
        setActiveProjectId(lastActive);
      } else {
        setActiveProjectId(null);
      }
    }
  }, [prodProjectId]);

  // Debounce timer for socket-driven refetch. Multiple PUTs in quick succession
  // (e.g. dragging items, or React StrictMode double-firing) would otherwise
  // queue several full refetches and replace state mid-render — which has
  // caused the ErrorBoundary to trigger ("Algo salió mal") when adding items
  // to a CUT. We coalesce all updates within 250ms into one refetch.
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const projectMutationVersionRef = useRef(0);
  const pendingItemOrdersRef = useRef(
    new Map<string, { revision: number; items: SelectableItem[] }>(),
  );
  const reorderSaveQueuesRef = useRef(new Map<string, Promise<boolean>>());

  // Day window the CUT list is scoped to, as epoch-ms [from, to). Held in a ref
  // (not state) because fetchProjects is called from socket handlers and timers
  // that captured an older closure — those must still see the current day.
  const cutDayWindowRef = useRef<{ from: number; to: number } | null>(null);
  const activeProjectIdRef = useRef<string | null>(activeProjectId);
  activeProjectIdRef.current = activeProjectId;

  const mergePendingItemOrders = (projects: AssemblyProject[]) =>
    projects.map((project) => {
      const pending = pendingItemOrdersRef.current.get(project.id);
      return pending ? { ...project, items: pending.items } : project;
    });

  const fetchProjects = async (approvedOnly = currentFilterApprovedOnly) => {
    const requestMutationVersion = projectMutationVersionRef.current;
    try {
      const cacheBuster = `&_t=${Date.now()}`;
      const fallbackKey = `braindog_last_cut_${prodProjectId || "global"}`;
      const lastActive = localStorage.getItem(fallbackKey);

      let url = `/api/assembly/projects?approvedOnly=${approvedOnly ? "true" : "false"}${prodProjectId ? `&projectId=${prodProjectId}` : ""}${cacheBuster}`;

      // Scope the CUT list to the day it actually renders. Fetching every corte
      // of the production and slicing a day in the browser meant a production
      // with >1000 cortes silently lost the overflow (PostgREST's row cap), so
      // whole days went missing from the list for every user.
      const dayWindow = approvedOnly ? null : cutDayWindowRef.current;
      if (dayWindow) {
        url += `&createdFrom=${dayWindow.from}&createdTo=${dayWindow.to}`;
        // The open corte may predate the selected day — keep it in the list so
        // the editor doesn't lose the project the user is working on.
        const keepId = activeProjectIdRef.current;
        if (keepId) url += `&includeId=${encodeURIComponent(keepId)}`;
      }

      const projectsRes = await authFetch(url);
      const projectsData = await projectsRes.json();
      // A request that started before a local reorder must never replace that
      // newer order, even if the reorder has already finished saving.
      if (projectsData.projects && requestMutationVersion === projectMutationVersionRef.current) {
        setAssemblyProjects(mergePendingItemOrders(projectsData.projects));
      }
    } catch (e) {
      console.error("Failed to load assembly projects:", e);
    }
  };

  // Called by the CUT list whenever its selected day changes. Refetches so the
  // new day's cortes come from the backend rather than being sliced locally.
  const setCutDayWindow = (window: { from: number; to: number } | null) => {
    const current = cutDayWindowRef.current;
    const same =
      (!current && !window) ||
      (!!current && !!window && current.from === window.from && current.to === window.to);
    if (same) return;
    cutDayWindowRef.current = window;
    void fetchProjects();
  };

  // Fetch data from API on mount
  useEffect(() => {
    const loadData = async () => {
      const requestMutationVersion = projectMutationVersionRef.current;
      let targetProjId: string | null = null;
      try {
        const ts = Date.now();

        // Obtenemos el ID de producción activo directamente de localStorage
        // para evitar el retardo del hook asíncrono
        const storedProd = localStorage.getItem("braindog_prodProjectId");
        let pId = null;
        if (storedProd) {
          try {
            const parsed = JSON.parse(storedProd);
            pId = typeof parsed === "object" ? parsed?.id : parsed;
          } catch (err) {
            pId = storedProd;
          }
        }
        targetProjId = pId;
        const fallbackKey = `braindog_last_cut_${targetProjId || "global"}`;
        const lastActive = localStorage.getItem(fallbackKey);

        // 1. Cargamos las carpetas y ÚNICAMENTE el último proyecto activo de forma exclusiva
        const foldersPromise = authFetch(
          targetProjId
            ? `/api/assembly/folders?projectId=${targetProjId}&_t=${ts}`
            : `/api/assembly/folders?_t=${ts}`,
        );

        let projectsPromise;
        if (lastActive) {
          projectsPromise = authFetch(
            `/api/assembly/projects?onlyActiveId=${lastActive}${targetProjId ? `&projectId=${targetProjId}` : ""}&_t=${ts}`,
          );
        } else {
          projectsPromise = authFetch(
            targetProjId
              ? `/api/assembly/projects?onlyLatest=true&projectId=${targetProjId}&_t=${ts}`
              : `/api/assembly/projects?onlyLatest=true&_t=${ts}`,
          );
        }

        const [foldersRes, projectsRes] = await Promise.all([foldersPromise, projectsPromise]);

        const foldersData = await foldersRes.json();
        const projectsData = await projectsRes.json();

        if (requestMutationVersion !== projectMutationVersionRef.current) {
          setIsLoaded(true);
          return;
        }

        if (foldersData.folders) setAssemblyFolders(foldersData.folders);

        if (projectsData.projects && projectsData.projects.length > 0) {
          setAssemblyProjects(mergePendingItemOrders(projectsData.projects));

          const projectExists = projectsData.projects.some((p: any) => p.id === lastActive);

          if (lastActive && projectExists) {
            console.debug(
              `[Selection] Restored and forced active project exclusively: ${lastActive}`,
            );
            setActiveProjectId(lastActive);
          } else {
            const defaultId = projectsData.projects[0].id;
            setActiveProjectId(defaultId);
            localStorage.setItem(fallbackKey, defaultId);
          }
        } else {
          await createAndSetDefaultProject(fallbackKey);
        }
        setIsLoaded(true);

        // --- FASE 2: Cargar el resto de proyectos en segundo plano ---
        // Al llamar a fetchProjects sin filtros excluyentes, poblará
        // la barra lateral silenciosamente con el resto de proyectos.
        setTimeout(() => {
          fetchProjects(false).catch((err) =>
            console.error("Error en Fase 2 de carga de proyectos:", err),
          );
        }, 100);
      } catch (e) {
        console.error("Failed to load assembly data:", e);
        setIsLoaded(true);
      }

      async function createAndSetDefaultProject(storageKey: string) {
        let currentUserName = user?.name;
        if (!currentUserName) {
          const storedUser = localStorage.getItem("auth_user");
          if (storedUser) {
            try {
              currentUserName = JSON.parse(storedUser).name;
            } catch (e) {}
          }
        }

        const projectName = currentUserName
          ? `${t("common.context.myProject")} - ${currentUserName}`
          : t("common.context.myProject");

        const defaultProject: AssemblyProject = {
          id: generateId(),
          name: projectName,
          items: [],
          createdAt: Date.now(),
          updatedAt: Date.now(),
          folderId: null,
          projectId: targetProjId || null,
        };
        setAssemblyProjects([defaultProject]);
        setActiveProjectId(defaultProject.id);
        localStorage.setItem(storageKey, defaultProject.id);
        authFetch(
          targetProjId
            ? `/api/assembly/projects?projectId=${targetProjId}`
            : "/api/assembly/projects",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(defaultProject),
          },
        ).catch(console.error);
      }
    };
    loadData();

    const handleUpdate = () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      refetchTimerRef.current = setTimeout(() => {
        refetchTimerRef.current = null;
        fetchProjects();
      }, 250);
    };

    socketService.on("assembly-projects-updated", handleUpdate);

    return () => {
      socketService.off("assembly-projects-updated", handleUpdate);
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
        refetchTimerRef.current = null;
      }
    };
  }, [t, currentFilterApprovedOnly, prodProjectId]);

  // Sync wrappers
  const syncProjectToBackend = async (
    method: "POST" | "PUT" | "DELETE",
    projectOrId: any,
  ): Promise<boolean> => {
    try {
      let url =
        method === "DELETE"
          ? `/api/assembly/projects/${projectOrId}`
          : method === "PUT"
            ? `/api/assembly/projects/${projectOrId.id}`
            : `/api/assembly/projects`;
      if (prodProjectId && method !== "DELETE") {
        url += url.includes("?") ? `&projectId=${prodProjectId}` : `?projectId=${prodProjectId}`;
      }
      const body = method === "DELETE" ? undefined : JSON.stringify(projectOrId);
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body,
      });
      // authFetch resolves on any HTTP status — surface 4xx/5xx explicitly
      // so a silently expired JWT or stale session doesn't leave the UI
      // showing optimistic updates that never landed on the server.
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        console.error(`[Selection] sync ${method} failed (${res.status}): ${text}`);
        Sentry.captureMessage(`[Selection] sync project ${method} ${res.status}`, {
          level: res.status === 401 || res.status === 403 ? "warning" : "error",
          tags: {
            scope: "selection_sync",
            http_status: String(res.status),
            method,
          },
          extra: {
            projectId: method === "DELETE" ? projectOrId : projectOrId?.id,
            responseBody: text?.slice(0, 500),
          },
        });
        // 401/403 means the session is dead — force a refetch which will
        // either reauth via the supabase refresh path or trigger the auth
        // context's revalidation, both of which recover the user.
        if (res.status === 401 || res.status === 403) {
          fetchProjects();
        }
        return false;
      }
      return true;
    } catch (e) {
      console.error("Failed to sync project", e);
      Sentry.captureException(e, {
        tags: { scope: "selection_sync", method },
        extra: { projectId: method === "DELETE" ? projectOrId : projectOrId?.id },
      });
      return false;
    }
  };

  const enqueueItemOrderSave = (projectId: string, items: SelectableItem[], revision: number) => {
    const previousSave = reorderSaveQueuesRef.current.get(projectId) ?? Promise.resolve(true);
    const save = previousSave
      .catch(() => false)
      .then(() => syncProjectToBackend("PUT", { id: projectId, items }));

    reorderSaveQueuesRef.current.set(projectId, save);

    void save
      .then((saved) => {
        const pending = pendingItemOrdersRef.current.get(projectId);
        if (pending?.revision !== revision) return;

        pendingItemOrdersRef.current.delete(projectId);
        if (!saved) {
          // The optimistic order could not be persisted. Reload the canonical
          // server state instead of leaving a misleading local arrangement.
          void fetchProjects();
        }
      })
      .finally(() => {
        if (reorderSaveQueuesRef.current.get(projectId) === save) {
          reorderSaveQueuesRef.current.delete(projectId);
        }
      });
  };

  const syncFolderToBackend = async (method: "POST" | "PUT" | "DELETE", folderOrId: any) => {
    try {
      let url =
        method === "DELETE"
          ? `/api/assembly/folders/${folderOrId}`
          : method === "PUT"
            ? `/api/assembly/folders/${folderOrId.id}`
            : `/api/assembly/folders`;
      if (prodProjectId && method !== "DELETE") {
        url += url.includes("?") ? `&projectId=${prodProjectId}` : `?projectId=${prodProjectId}`;
      }
      const body = method === "DELETE" ? undefined : JSON.stringify(folderOrId);
      await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body,
      });
    } catch (e) {
      console.error("Failed to sync folder", e);
    }
  };

  // Ensure activeProjectId is valid
  useEffect(() => {
    if (!isLoaded || assemblyProjects.length === 0) return;
    if (activeProjectId && !assemblyProjects.find((p) => p.id === activeProjectId)) {
      // Try to restore from localStorage first before falling back to [0]
      const storageKey = `braindog_last_cut_${prodProjectId || "global"}`;
      const lastActive = localStorage.getItem(storageKey);
      const exists = lastActive && assemblyProjects.find((p) => p.id === lastActive);
      console.debug(
        `[Selection] VALIDATION: activeProjectId ${activeProjectId} is INVALID. Restoring from ${storageKey}=${lastActive} (exists: ${exists})`,
      );
      if (exists) {
        setActiveProjectId(lastActive);
      } else {
        const defaultId = assemblyProjects.length > 0 ? assemblyProjects[0].id : null;
        setActiveProjectId(defaultId);
        if (defaultId) localStorage.setItem(storageKey, defaultId);
      }
    } else if (!activeProjectId && assemblyProjects.length > 0) {
      const storageKey = `braindog_last_cut_${prodProjectId || "global"}`;
      const lastActive = localStorage.getItem(storageKey);
      const exists = lastActive && assemblyProjects.find((p) => p.id === lastActive);
      console.debug(
        `[Selection] VALIDATION: activeProjectId is NULL. Restoring from ${storageKey}=${lastActive} (exists: ${exists})`,
      );
      if (exists) {
        setActiveProjectId(lastActive);
      } else {
        const defaultId = assemblyProjects[0].id;
        setActiveProjectId(defaultId);
        localStorage.setItem(storageKey, defaultId);
      }
    } else {
      console.debug(`[Selection] VALIDATION: activeProjectId ${activeProjectId} is VALID.`);
    }
  }, [assemblyProjects, activeProjectId, isLoaded, prodProjectId]);

  const toggleSelectionMode = () => {
    setIsSelectionMode((prev) => !prev);
  };

  const toggleItem = (item: SelectableItem) => {
    setSelectedItems((prev) => {
      const exists = prev.some(
        (i) => i.id === item.id || i.sourceId === item.id || i.storyId === item.id,
      );
      if (exists) {
        return prev.filter(
          (i) => i.id !== item.id && i.sourceId !== item.id && i.storyId !== item.id,
        );
      } else {
        const isVtrOrEvent = ["rundown", "event", "vtr"].includes(item.type);
        const newItem = {
          ...item,
          sourceId: item.sourceId || item.id,
          storyId: item.type === "story" ? item.storyId || item.id : item.storyId,
          type: isVtrOrEvent ? "story" : item.type,
        };
        return [...prev, newItem];
      }
    });
  };

  const toggleAssemblyModal = () => {
    setIsAssemblyModalOpen((prev) => !prev);
  };

  const moveItem = (fromIndex: number, toIndex: number) => {
    setSelectedItems((prev) => {
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      const newItems = [...prev];
      const [moved] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, moved);
      return newItems;
    });
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const isSelected = (id: string) => {
    return selectedItems.some((i) => i.id === id || i.sourceId === id || i.storyId === id);
  };

  // Project Management
  const createProject = (name: string, folderId?: string | null): AssemblyProject => {
    const fallbackName = user?.name
      ? `${t("common.context.newProject")} - ${user.name}`
      : t("common.context.newProject");

    const newProject: AssemblyProject = {
      id: generateId(),
      name: name || fallbackName,
      items: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      folderId: folderId || null,
      isApproved: false,
    };
    setAssemblyProjects((prev) => [...prev, newProject]);
    syncProjectToBackend("POST", newProject);
    return newProject;
  };

  const deleteProject = (id: string) => {
    setAssemblyProjects((prev) => prev.filter((p) => p.id !== id));
    syncProjectToBackend("DELETE", id);
  };

  const renameProject = (id: string, name: string) => {
    let proj: AssemblyProject | undefined;
    setAssemblyProjects((prev) => {
      const updated = prev.map((p) => (p.id === id ? { ...p, name, updatedAt: Date.now() } : p));
      proj = updated.find((p) => p.id === id);
      return updated;
    });
    if (proj) syncProjectToBackend("PUT", proj);
  };

  const updateProject = (id: string, updates: Partial<AssemblyProject>) => {
    setAssemblyProjects((prev) => {
      return prev.map((p) => (p.id === id ? { ...p, ...updates, updatedAt: Date.now() } : p));
    });
    syncProjectToBackend("PUT", { id, ...updates });
  };

  const saveActiveProjectToMemory = (id: string) => {
    const currentPId = prodProjectId || "global";
    const storageKey = `braindog_last_cut_${currentPId}`;
    localStorage.setItem(storageKey, id);
  };

  const setActiveProject = (id: string | null) => {
    setActiveProjectId(id);
    if (id) {
      saveActiveProjectToMemory(id);
      const requestMutationVersion = projectMutationVersionRef.current;

      // Forzar carga detallada del proyecto seleccionado (con words)
      // para asegurar que el editor de cortes tenga todos los datos necesarios
      authFetch(
        `/api/assembly/projects?id=${id}${prodProjectId ? `&projectId=${prodProjectId}` : ""}&_t=${Date.now()}`,
      )
        .then((res) => {
          if (res.ok) return res.json();
          throw new Error("Failed detailed load");
        })
        .then((data) => {
          if (
            data.projects &&
            data.projects.length > 0 &&
            requestMutationVersion === projectMutationVersionRef.current
          ) {
            const detailedProj = data.projects[0];
            setAssemblyProjects((prev) =>
              prev.map((p) =>
                p.id === detailedProj.id ? mergePendingItemOrders([detailedProj])[0] : p,
              ),
            );
          }
        })
        .catch((e) => console.error("Error loading detailed active project:", e));
    }
  };

  // Carga asíncrona detallada (con words) para el activeProjectId restaurado síncronamente
  useEffect(() => {
    if (activeProjectId && isLoaded) {
      const requestMutationVersion = projectMutationVersionRef.current;
      // Disparamos la recarga del activo para traer las transcripciones de las words
      authFetch(
        `/api/assembly/projects?id=${activeProjectId}${prodProjectId ? `&projectId=${prodProjectId}` : ""}&_t=${Date.now()}`,
      )
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (
            data?.projects?.length > 0 &&
            requestMutationVersion === projectMutationVersionRef.current
          ) {
            const detailedProj = data.projects[0];
            setAssemblyProjects((prev) =>
              prev.map((p) =>
                p.id === detailedProj.id ? mergePendingItemOrders([detailedProj])[0] : p,
              ),
            );
          }
        })
        .catch(() => {});
    }
  }, [activeProjectId, isLoaded, prodProjectId]);

  const getActiveProject = (): AssemblyProject | null => {
    return assemblyProjects.find((p) => p.id === activeProjectId) || null;
  };

  const addItemsToProject = (
    projectId: string,
    items: SelectableItem[],
    insertAtIndex?: number,
  ) => {
    let added = 0;
    let updatedProj: AssemblyProject | null = null;
    let projectFound = false;
    setAssemblyProjects((prev) => {
      let next: AssemblyProject | null = null;
      const updated = prev.map((p) => {
        if (p.id !== projectId) return p;
        projectFound = true;
        // Build dedup keys per asset. For logs/transcripts, sourceId points
        // at the parent story and is shared across siblings; tc is not unique
        // either (two logs created in the same second, or two transcript
        // segments at the same timecode with different speakers, collide).
        // We add author + a slice of the text so distinct entries at the same
        // tc don't get wrongly flagged as duplicates.
        const assetKey = (i: SelectableItem) => {
          if (i.type === "log" || i.type === "transcript" || i.type === "otf") {
            const text = (i.selectedText || i.title || "").slice(0, 80);
            return `${i.type}:${i.sourceId || i.storyId || ""}:${i.tc || ""}:${i.author || ""}:${text}`;
          }
          return `${i.type}:${i.sourceId || i.storyId || i.id}`;
        };
        const existingAssetKeys = new Set(p.items.map(assetKey));
        const newItems = items
          .filter((i) => !existingAssetKeys.has(assetKey(i)))
          .map((i) => {
            const isVtrOrEvent = ["rundown", "event", "vtr"].includes(i.type);
            return {
              ...i,
              id: crypto.randomUUID(),
              sourceId: i.sourceId || i.id,
              storyId: i.type === "story" ? i.storyId || i.id : i.storyId,
              type: isVtrOrEvent ? "story" : i.type,
            };
          });
        added = newItems.length;
        // Insert at the specified index, or append to the end if not specified
        let updatedItems: SelectableItem[];
        if (insertAtIndex !== undefined && insertAtIndex >= 0 && insertAtIndex <= p.items.length) {
          updatedItems = [...p.items];
          updatedItems.splice(insertAtIndex, 0, ...newItems);
        } else {
          updatedItems = [...p.items, ...newItems];
        }
        next = {
          ...p,
          items: updatedItems,
          updatedAt: Date.now(),
        };
        return next;
      });
      updatedProj = next;
      return updated;
    });
    if (updatedProj) {
      Sentry.addBreadcrumb({
        category: "selection",
        level: "info",
        message: "addItemsToProject",
        data: { projectId, added, totalItems: items.length },
      });
      syncProjectToBackend("PUT", updatedProj);
    } else if (!projectFound) {
      // Local state is stale (project was deleted server-side, or scope
      // changed after a user/session switch). Force a refetch so the next
      // attempt finds a valid project.
      console.warn(
        `[Selection] addItemsToProject: project ${projectId} not in local state — refetching`,
      );
      Sentry.captureMessage("[Selection] addItemsToProject: stale activeProjectId", {
        level: "warning",
        tags: { scope: "selection_stale" },
        extra: { projectId, itemsCount: items.length },
      });
      fetchProjects();
    }
    return { added, skipped: items.length - added };
  };

  const removeItemFromProject = (projectId: string, itemId: string) => {
    let updatedProj: AssemblyProject | null = null;
    setAssemblyProjects((prev) => {
      let next: AssemblyProject | null = null;
      const updated = prev.map((p) => {
        if (p.id !== projectId) return p;
        next = {
          ...p,
          items: p.items.filter((i) => i.id !== itemId),
          updatedAt: Date.now(),
        };
        return next;
      });
      updatedProj = next;
      return updated;
    });
    if (updatedProj) syncProjectToBackend("PUT", updatedProj);
  };

  const updateItemInProject = (
    projectId: string,
    itemId: string,
    updates: Partial<SelectableItem>,
  ) => {
    let updatedProj: AssemblyProject | null = null;
    setAssemblyProjects((prev) => {
      let next: AssemblyProject | null = null;
      const updated = prev.map((p) => {
        if (p.id !== projectId) return p;
        next = {
          ...p,
          items: p.items.map((i) => (i.id === itemId ? { ...i, ...updates } : i)),
          updatedAt: Date.now(),
        };
        return next;
      });
      updatedProj = next;
      return updated;
    });
    if (updatedProj) syncProjectToBackend("PUT", updatedProj);
  };

  const moveItemInProject = (projectId: string, fromIndex: number, toIndex: number) => {
    const project = assemblyProjectsRef.current.find((candidate) => candidate.id === projectId);
    if (
      !project ||
      fromIndex < 0 ||
      fromIndex >= project.items.length ||
      toIndex < 0 ||
      toIndex >= project.items.length ||
      fromIndex === toIndex
    ) {
      return;
    }

    const newItems = [...project.items];
    const [moved] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, moved);

    const revision = ++projectMutationVersionRef.current;
    pendingItemOrdersRef.current.set(projectId, { revision, items: newItems });

    const nextProjects = assemblyProjectsRef.current.map((candidate) =>
      candidate.id === projectId
        ? { ...candidate, items: newItems, updatedAt: Date.now() }
        : candidate,
    );
    assemblyProjectsRef.current = nextProjects;
    setAssemblyProjects(nextProjects);
    enqueueItemOrderSave(projectId, newItems, revision);
  };

  // Folder Management
  const createFolder = (name: string, parentId?: string | null): AssemblyFolder => {
    const newFolder: AssemblyFolder = {
      id: generateFolderId(),
      name: name || t("common.context.newFolder"),
      createdAt: Date.now(),
      isExpanded: true,
      parentId: parentId || null,
    };
    setAssemblyFolders((prev) => [...prev, newFolder]);
    syncFolderToBackend("POST", newFolder);
    return newFolder;
  };

  const deleteFolder = (id: string) => {
    // Move all projects in this folder to root
    const projectsToSync: AssemblyProject[] = [];
    setAssemblyProjects((prev) => {
      const moved: AssemblyProject[] = [];
      const updated = prev.map((p) => {
        if (p.folderId === id) {
          const mod = { ...p, folderId: null };
          moved.push(mod);
          return mod;
        }
        return p;
      });
      projectsToSync.splice(0, projectsToSync.length, ...moved);
      return updated;
    });
    projectsToSync.forEach((p) => syncProjectToBackend("PUT", p));

    // Move sub-folders to root
    const foldersToSync: AssemblyFolder[] = [];
    setAssemblyFolders((prev) => {
      const moved: AssemblyFolder[] = [];
      const updated = prev.map((f) => {
        if (f.parentId === id) {
          const mod = { ...f, parentId: null };
          moved.push(mod);
          return mod;
        }
        return f;
      });
      foldersToSync.splice(0, foldersToSync.length, ...moved);
      return updated.filter((f) => f.id !== id);
    });
    foldersToSync.forEach((f) => syncFolderToBackend("PUT", f));

    syncFolderToBackend("DELETE", id);
  };

  const renameFolder = (id: string, name: string) => {
    let fold: AssemblyFolder | undefined;
    setAssemblyFolders((prev) => {
      const updated = prev.map((f) => (f.id === id ? { ...f, name } : f));
      fold = updated.find((f) => f.id === id);
      return updated;
    });
    if (fold) syncFolderToBackend("PUT", fold);
  };

  const toggleFolderExpanded = (id: string) => {
    setAssemblyFolders((prev) =>
      prev.map((f) => (f.id === id ? { ...f, isExpanded: !f.isExpanded } : f)),
    );
  };

  const moveProjectToFolder = (projectId: string, folderId: string | null) => {
    let proj: AssemblyProject | undefined;
    setAssemblyProjects((prev) => {
      const updated = prev.map((p) =>
        p.id === projectId ? { ...p, folderId, updatedAt: Date.now() } : p,
      );
      proj = updated.find((p) => p.id === projectId);
      return updated;
    });
    if (proj) syncProjectToBackend("PUT", proj);
  };

  const moveFolderToFolder = (folderId: string, parentId: string | null) => {
    // Basic cycle prevention: a folder cannot be its own parent
    if (folderId === parentId) return;

    // More robust cycle prevention: Ensure we are not moving a folder into one of its descendants
    const isDescendant = (potentialDescendantId: string | null, ancestorId: string): boolean => {
      let currentId = potentialDescendantId;
      while (currentId != null) {
        if (currentId === ancestorId) return true;
        const parent = assemblyFolders.find((f) => f.id === currentId);
        currentId = parent?.parentId || null;
      }
      return false;
    };

    if (isDescendant(parentId, folderId)) return;

    let fold: AssemblyFolder | undefined;
    setAssemblyFolders((prev) => {
      const updated = prev.map((f) => (f.id === folderId ? { ...f, parentId } : f));
      fold = updated.find((f) => f.id === folderId);
      return updated;
    });
    if (fold) syncFolderToBackend("PUT", fold);
  };

  const setNavigateToSource = (target: NavigateTarget) => {
    setNavigateToSourceState(target);
  };

  const clearNavigateToSource = () => {
    setNavigateToSourceState(null);
  };

  return (
    <SelectionContext.Provider
      value={{
        isSelectionMode,
        toggleSelectionMode,
        selectedItems,
        toggleItem,
        clearSelection,
        isAssemblyModalOpen,
        toggleAssemblyModal,
        moveItem,
        isSelected,
        // Projects
        assemblyProjects,
        activeProjectId,
        setActiveProject,
        createProject,
        deleteProject,
        renameProject,
        updateProject,
        addItemsToProject,
        removeItemFromProject,
        updateItemInProject,
        moveItemInProject,
        getActiveProject,
        // Folders
        assemblyFolders,
        activeFolderId,
        setActiveFolder: setActiveFolderId,
        createFolder,
        deleteFolder,
        renameFolder,
        toggleFolderExpanded,
        moveProjectToFolder,
        moveFolderToFolder,
        // Navigate to source
        navigateToSource,
        setNavigateToSource,
        clearNavigateToSource,
        // Loading state
        isLoaded,
        // Backend fetching
        fetchProjects,
        setCutDayWindow,
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};

export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error("useSelection must be used within a SelectionProvider");
  }
  return context;
};
